import { buttonVariants } from "@nt/ui/recipes";
import { ErrorState, LoadingState } from "@nt/ui/states";
import { Navigate, Route, Routes } from "react-router";

import { PartnerShell } from "@/components/layout/partner-shell";
import { ApiError } from "@/lib/api";
import { useSession, type Session } from "@/lib/queries";
import { signOut } from "@/lib/sign-out";
import { Customers } from "@/pages/customers";
import { CustomerDetailPage } from "@/pages/customers/detail";
import { Links } from "@/pages/links";
import { Notice } from "@/pages/notice";
import { Overview } from "@/pages/overview";
import { Payouts } from "@/pages/payouts";
import { Performance } from "@/pages/performance";
import { Rates } from "@/pages/rates";
import { Resources } from "@/pages/resources";
import { SettingsPage } from "@/pages/settings";
import { SignIn } from "@/pages/sign-in";

/** Old portal paths (emails and the API's `nextStep.to` may still carry them). */
export const REDIRECTS: Readonly<Record<string, string>> = {
  "/referrals": "/customers",
  "/payout-method": "/settings/payout-method",
  "/tax-form": "/settings/tax-form",
};

/**
 * Every signed-in route (spec: Information architecture), plus the old paths
 * that now redirect. Exported so a test can walk it without a session fetch.
 */
export function PortalRoutes({ session }: { session: Session }) {
  const readOnly = session.state === "read_only";
  return (
    <Routes>
      <Route path="/" element={<Overview />} />
      <Route path="/performance" element={<Performance />} />
      <Route path="/performance/:tab" element={<Performance />} />
      <Route path="/links" element={<Links />} />
      <Route path="/customers" element={<Customers />} />
      <Route path="/customers/:id" element={<CustomerDetailPage />} />
      <Route path="/payouts" element={<Payouts />} />
      <Route path="/rates" element={<Rates />} />
      <Route path="/resources" element={<Resources />} />
      <Route
        path="/settings"
        element={<SettingsPage session={session} readOnly={readOnly} />}
      />
      <Route
        path="/settings/:tab"
        element={<SettingsPage session={session} readOnly={readOnly} />}
      />
      {Object.entries(REDIRECTS).map(([from, to]) => (
        <Route key={from} path={from} element={<Navigate to={to} replace />} />
      ))}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App() {
  const session = useSession();
  if (session.isPending) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <LoadingState label="Loading your account" />
      </div>
    );
  }
  // Not a sign-in problem: a code here would put a normal session over the staff one.
  if (
    session.error instanceof ApiError &&
    session.error.code === "IMPERSONATED"
  ) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <ErrorState
          title="This session cannot open the portal"
          description={session.error.message}
          recovery="Sign out, then sign in with your own partner email."
          action={
            <button
              type="button"
              className={buttonVariants()}
              onClick={() => void signOut()}
            >
              Sign out
            </button>
          }
        />
      </div>
    );
  }
  if (
    session.error instanceof ApiError &&
    (session.error.status === 401 || session.error.status === 403)
  ) {
    return <SignIn />;
  }
  if (session.isError || !session.data) {
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <ErrorState
          title="We could not open the portal"
          description={
            session.error instanceof Error ? session.error.message : undefined
          }
          recovery="Check your connection and try again. If it keeps failing, write to support@nonresident.tax."
          onRetry={() => void session.refetch()}
        />
      </div>
    );
  }
  const s = session.data;
  if (s.state === "pending" || s.state === "none" || s.state === "closed") {
    return <Notice state={s.state} email={s.email} />;
  }
  return (
    <PartnerShell session={s}>
      <PortalRoutes session={s} />
    </PartnerShell>
  );
}
